import { Fragment } from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { HoverHintTrigger } from "@/components/ui/title-help";
import { cn } from "@/lib/utils";
import { warehouseRecountMpnContainerHoverHint } from "@/lib/warehouseRecountMpnContainerHint";
import {
  filterAndSortWarehouseRecountLocationSummaries,
  isPrimaryWarehouseHubLocationId,
} from "@/lib/warehouseHubLocationSelect";
import type {
  WarehouseHubLocation,
  WarehouseRecountLocationSummary,
  WarehouseRecountMpnBreakdownRow,
} from "@/lib/warehouseRecountApi";

function formatQuantity(value: number): string {
  return value.toLocaleString();
}

function formatInventoryContainerCount(count: number): string {
  const quantity = Math.trunc(count);
  if (quantity <= 0) {
    return "—";
  }
  if (quantity === 1) {
    return "1 container";
  }
  return `${quantity.toLocaleString()} containers`;
}

function locationLabel(
  hubLocationId: number,
  locationName: string | null,
  warehouseLocations: readonly WarehouseHubLocation[],
): string {
  if (locationName != null && locationName.trim() !== "") {
    return locationName;
  }
  const match = warehouseLocations.find((location) => location.hub_location_id === hubLocationId);
  if (match?.location_name != null && match.location_name.trim() !== "") {
    return match.location_name;
  }
  return `Warehouse ${String(hubLocationId)}`;
}

function MpnBreakdownRows(props: {
  rows: readonly WarehouseRecountMpnBreakdownRow[];
  onContainerSelect?: (boxHex: string) => void;
}): JSX.Element {
  if (props.rows.length === 0) {
    return (
      <tr>
        <td colSpan={4} className="px-3 py-2 text-sm text-slate-500">
          No containers or unclaimed bulk at this warehouse.
        </td>
      </tr>
    );
  }
  return (
    <>
      {props.rows.map((row) => (
        <tr key={`${row.approved_component_id ?? "unknown"}-${row.mpn_label}`} className="bg-slate-50/80">
          <td className="px-3 py-1.5 pl-8 text-sm text-slate-700">{row.mpn_label}</td>
          <td className="px-3 py-1.5 text-right text-sm tabular-nums text-slate-800">
            {formatQuantity(row.on_hand_quantity)}
          </td>
          <td className="px-3 py-1.5 text-right text-sm tabular-nums text-slate-800">
            {formatQuantity(row.transient_quantity)}
          </td>
          <td className="px-3 py-1.5 text-right text-sm tabular-nums text-slate-600">
            {row.carton_count > 0 ? (
              <HoverHintTrigger
                align="end"
                openOnTriggerActivate
                trigger={
                  <button
                    type="button"
                    className="min-h-11 rounded px-2 tabular-nums text-brand-700 underline decoration-dotted underline-offset-2 hover:bg-brand-50"
                    aria-label={`${formatInventoryContainerCount(row.carton_count)}. Show containers.`}
                  >
                    {formatInventoryContainerCount(row.carton_count)}
                  </button>
                }
              >
                {warehouseRecountMpnContainerHoverHint({
                  containers: row.containers ?? [],
                  onSelectContainer: props.onContainerSelect,
                })}
              </HoverHintTrigger>
            ) : (
              formatInventoryContainerCount(row.carton_count)
            )}
          </td>
        </tr>
      ))}
    </>
  );
}

export function WarehouseRecountLocationPanel(props: {
  warehouseLocations: readonly WarehouseHubLocation[];
  skuLocations: readonly WarehouseRecountLocationSummary[];
  hubLocationId: number | null;
  mpnBreakdown: readonly WarehouseRecountMpnBreakdownRow[];
  siteDetailLoading?: boolean;
  showNonPrimarySites: boolean;
  disabled?: boolean;
  onShowNonPrimarySitesChange: (show: boolean) => void;
  onHubLocationIdChange: (hubLocationId: number) => void;
  onContainerSelect?: (boxHex: string) => void;
}): JSX.Element {
  const visibleLocations = filterAndSortWarehouseRecountLocationSummaries(props.skuLocations, {
    showNonPrimarySites: props.showNonPrimarySites,
  });
  const totalOnHand = visibleLocations.reduce((sum, location) => sum + location.on_hand, 0);
  const totalTransient = visibleLocations.reduce((sum, location) => sum + location.transient_quantity, 0);
  const totalContainers = visibleLocations.reduce((sum, location) => sum + location.boxed_total, 0);
  const hiddenNonPrimaryCount = props.skuLocations.filter(
    (row) => !isPrimaryWarehouseHubLocationId(row.hub_location_id),
  ).length;

  return (
    <div className="space-y-3">
      {hiddenNonPrimaryCount > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-slate-600">Sites</span>
          <Button
            type="button"
            size="sm"
            variant={props.showNonPrimarySites ? "primary" : "secondary"}
            disabled={props.disabled}
            onClick={() => props.onShowNonPrimarySitesChange(!props.showNonPrimarySites)}
          >
            {props.showNonPrimarySites ? "Hide other sites" : "Show other sites"}
          </Button>
          {!props.showNonPrimarySites ? (
            <span className="text-xs text-slate-500">
              {String(hiddenNonPrimaryCount)} non-primary site
              {hiddenNonPrimaryCount === 1 ? "" : "s"} hidden
            </span>
          ) : null}
        </div>
      ) : null}

      {visibleLocations.length > 0 ? (
        <div className="overflow-x-auto rounded-md border border-slate-200">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2 font-medium">Warehouse</th>
                <th className="px-3 py-2 font-medium text-right">On-hand</th>
                <th className="px-3 py-2 font-medium text-right">Transient</th>
                <th className="px-3 py-2 font-medium text-right">Containers</th>
              </tr>
            </thead>
            <tbody>
              {visibleLocations.map((location) => {
                const selected = props.hubLocationId === location.hub_location_id;
                return (
                  <Fragment key={location.hub_location_id}>
                    <tr
                      className={cn(
                        "h-12 border-t border-slate-200",
                        selected ? "bg-brand-50" : "bg-white",
                        props.disabled ? "opacity-60" : "cursor-pointer hover:bg-slate-50",
                      )}
                      onClick={() => {
                        if (props.disabled) return;
                        props.onHubLocationIdChange(location.hub_location_id);
                      }}
                    >
                      <td className="px-3 py-2 font-medium text-slate-900">
                        {locationLabel(
                          location.hub_location_id,
                          location.location_name,
                          props.warehouseLocations,
                        )}
                        {isPrimaryWarehouseHubLocationId(location.hub_location_id) ? null : (
                          <span className="ml-2 text-xs font-normal text-slate-500">Other site</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatQuantity(location.on_hand)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatQuantity(location.transient_quantity)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatQuantity(location.boxed_total)}</td>
                    </tr>
                    {selected && props.siteDetailLoading ? (
                      <tr className="border-t border-slate-200 bg-slate-50/80">
                        <td colSpan={4} className="px-3 py-2 text-sm text-slate-500">
                          <span className="inline-flex items-center gap-2">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                            Loading MPN breakdown.
                          </span>
                        </td>
                      </tr>
                    ) : null}
                    {selected && !props.siteDetailLoading ? (
                      <MpnBreakdownRows
                        rows={props.mpnBreakdown}
                        onContainerSelect={props.onContainerSelect}
                      />
                    ) : null}
                  </Fragment>
                );
              })}
              <tr className="h-12 border-t border-slate-300 bg-slate-50 font-semibold">
                <td className="px-3 py-2 text-slate-900">Total</td>
                <td className="px-3 py-2 text-right tabular-nums">{formatQuantity(totalOnHand)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{formatQuantity(totalTransient)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{formatQuantity(totalContainers)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-slate-600">No on-hand or labeled containers for this SKU at any warehouse site.</p>
      )}
    </div>
  );
}
